import { WidgetState, WidgetSkeleton, WidgetIssueState } from "@nexus/widget-sdk";
import { Flag } from "lucide-react";
import {
  firstError,
  HeaderStats,
  unwrap,
  useAssetUrl,
  useHeaderSummaryShown,
  Widget,
} from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { CDN_GATEKEEPER_RESPONSES_CONTRACT } from "./types";
import type { CdnGatekeeperResponsesData, CdnGatekeeperService } from "./types";

export interface CdnGatekeeperResponsesConfig {
  title?: string;
}

export const manifest: WidgetManifest = {
  id: "cdn-gatekeeper-responses",
  name: "CDN Gatekeeper responses",
  description: "One protected event's automated CDN Gatekeeper response outcomes.",
  version: "1.0.0",
  dataRequests: {
    responses: {
      source: "protection-overview-event-cdn-gatekeeper",
      contract: CDN_GATEKEEPER_RESPONSES_CONTRACT,
      // The dashboard instance binds this to
      // "protection-overview-event-cdn-gatekeeper" with a report_instance_id
      // param (see domains.dashboards.builtins). A dataBindings entry only
      // takes effect when the request declares itself configurable — "false"
      // here silently produced "Data request does not allow a source
      // override" and no data ever loaded.
      configurable: true,
    },
  },
};

export const defaultConfig: CdnGatekeeperResponsesConfig = {};

const percentFormat = new Intl.NumberFormat("en", { style: "percent", maximumFractionDigits: 0 });

function stepCellClass(isTrigger: boolean, isAfter: boolean): string {
  if (isTrigger) return "border-2 border-status-error bg-status-error-bg";
  return isAfter
    ? "border border-border bg-status-neutral/10"
    : "border border-border bg-surface-elevated";
}

function StepFrame({ reference, step }: { reference: string | undefined; step: number }) {
  const url = useAssetUrl(reference);
  if (!url) return null;
  return (
    <img
      src={url}
      alt={`Stream at step ${step + 1}`}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover"
    />
  );
}

function StepTimeline({
  stepCount,
  triggerStepIndex,
  frames,
}: {
  stepCount: number;
  triggerStepIndex: number;
  frames?: string[];
}) {
  const withFrames = Boolean(frames?.length);
  return (
    <div
      className="flex items-center gap-1"
      role="img"
      aria-label={`Response triggered at step ${triggerStepIndex + 1} of ${stepCount}`}
    >
      {Array.from({ length: stepCount }, (_, step) => {
        const isTrigger = step === triggerStepIndex;
        const isAfter = step > triggerStepIndex;
        return (
          <div key={step} className="flex min-w-0 flex-1 flex-col items-center gap-1">
            <div
              className={`relative flex w-full items-center justify-center overflow-hidden rounded-md ${withFrames ? "aspect-video" : "h-9"} ${stepCellClass(isTrigger, isAfter)}`}
            >
              {withFrames ? <StepFrame reference={frames?.[step]} step={step} /> : null}
              {isTrigger ? (
                <Flag
                  className={`relative h-4 w-4 text-status-error ${withFrames ? "rounded-sm bg-surface/90 p-0.5" : ""}`}
                  aria-hidden
                />
              ) : null}
            </div>
            <span className="text-2xs text-muted">{step + 1}</span>
          </div>
        );
      })}
    </div>
  );
}

function ServiceRow({
  service,
  stepCount,
  triggerStepIndex,
}: {
  service: CdnGatekeeperService;
  stepCount: number;
  triggerStepIndex: number;
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-border bg-surface p-3">
      <span className="truncate text-sm font-medium text-primary">{service.label}</span>
      <StepTimeline
        stepCount={stepCount}
        triggerStepIndex={triggerStepIndex}
        frames={service.frames}
      />
    </div>
  );
}

export function Component({ data, config }: WidgetProps<CdnGatekeeperResponsesConfig>) {
  const error = firstError(data, ["responses"]);
  const result = unwrap<CdnGatekeeperResponsesData>(data.responses);
  const loading = data.responses?.isLoading && !result;
  const heading = config.title ?? manifest.name;
  const summaryInHeader = useHeaderSummaryShown();
  const withFrames = result?.services.some((service) => service.frames?.length) ?? false;

  return (
    <Widget
      title={heading}
      headerSummary={
        result ? (
          <HeaderStats
            label={`${heading} summary`}
            items={[
              { label: "Response type", value: result.responseType, format: "text" },
              { label: "Response rate", value: result.responseRate, format: "percent" },
              { label: "Coverage rate", value: result.responseCoverageRate, format: "percent" },
            ]}
          />
        ) : undefined
      }
    >
      {error ? (
        <WidgetIssueState error={error} />
      ) : loading || !result ? (
        <WidgetSkeleton shape="list" />
      ) : result.services.length === 0 ? (
        <WidgetState title="No CDN Gatekeeper responses recorded for this event." />
      ) : (
        <div className="flex flex-col gap-4 density-widget-body">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            {summaryInHeader ? null : (
              <>
                <span className="rounded-full bg-surface-elevated px-2.5 py-1 text-2xs font-medium uppercase tracking-wider text-secondary">
                  {result.responseType}
                </span>
                <span className="text-secondary">
                  Response rate{" "}
                  <span className="font-semibold tabular-nums text-primary">
                    {percentFormat.format(result.responseRate)}
                  </span>
                </span>
                <span className="text-secondary">
                  Coverage{" "}
                  <span className="font-semibold tabular-nums text-primary">
                    {percentFormat.format(result.responseCoverageRate)}
                  </span>
                </span>
              </>
            )}
            <span className="flex items-center gap-1.5 text-2xs text-muted">
              <Flag className="h-3.5 w-3.5 text-status-error" aria-hidden />
              First disrupted frame
            </span>
          </div>
          <div
            className={`grid grid-cols-1 gap-2 ${withFrames ? "@4xl/widget:grid-cols-2" : "@3xl/widget:grid-cols-2"}`}
          >
            {result.services.map((service) => (
              <ServiceRow
                key={service.id}
                service={service}
                stepCount={result.stepCount}
                triggerStepIndex={result.triggerStepIndex}
              />
            ))}
          </div>
        </div>
      )}
    </Widget>
  );
}

const widgetModule: WidgetModule<CdnGatekeeperResponsesConfig> = {
  manifest,
  Component,
  defaultConfig,
};
export default widgetModule;
